import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "@/components/ui/Button";
import { getV2Error } from "@/services/api/v2";
import {
  MatchRating,
  MatchSquadMember,
  matchKeys,
  matchesV2API,
} from "@/services/api/matchesV2";
import SectionQuery from "../detail/SectionQuery";
import { useMatchDirtyGuard } from "@/context/MatchRuntimeContext";

const RatingsEditor: React.FC<{
  workspaceId: string;
  id: string;
  canEdit: boolean;
  reviewExists: boolean;
}> = ({ workspaceId, id, canEdit, reviewExists }) => {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: matchKeys.section(workspaceId, "manual", id, "ratings"),
    queryFn: () => matchesV2API.ratings(workspaceId, id),
  });
  const squad = useQuery({
    queryKey: matchKeys.section(workspaceId, "manual", id, "squad"),
    queryFn: () => matchesV2API.squad(workspaceId, id),
  });
  const [ratings, setRatings] = useState<MatchRating[]>([]);
  const [dirty, setDirty] = useState(false);
  useMatchDirtyGuard(`match-ratings-${id}`, dirty, "rating changes");
  useEffect(() => {
    if (query.data && !dirty) setRatings(query.data.data);
  }, [dirty, query.data]);
  const save = useMutation({
    mutationFn: () => matchesV2API.saveRatings(workspaceId, id, ratings),
    onSuccess: (value) => {
      client.setQueryData(
        matchKeys.section(workspaceId, "manual", id, "ratings"),
        value,
      );
      setRatings(value.data);
      setDirty(false);
    },
  });
  const change = (index: number, patch: Partial<MatchRating>) => {
    const next = [...ratings];
    next[index] = { ...next[index], ...patch };
    setRatings(next);
    setDirty(true);
  };
  const writable = canEdit && reviewExists;
  return (
    <SectionQuery title="Player ratings" query={query}>
      <div className="space-y-3">
        {canEdit && !reviewExists && (
          <p className="rounded-lg bg-amber-50 p-3 text-amber-900 dark:bg-amber-950 dark:text-amber-100">
            Save the match review before adding ratings.
          </p>
        )}
        {writable && (
          <label className="block max-w-md text-sm font-medium">
            Add squad player
            <select
              className="mt-1 min-h-11 w-full rounded-md border bg-white px-3 dark:border-gray-700 dark:bg-gray-900"
              value=""
              onChange={(event) => {
                const player = squad.data?.data.find(
                  (value: MatchSquadMember) =>
                    value.player.id === event.target.value,
                )?.player;
                if (
                  player &&
                  !ratings.some((value) => value.player.id === player.id)
                ) {
                  setRatings((value) => [
                    ...value,
                    { player, rating: 5, notes: "" },
                  ]);
                  setDirty(true);
                }
              }}
            >
              <option value="">Select player...</option>
              {squad.data?.data.map((value: MatchSquadMember) => (
                <option
                  key={value.player.id}
                  value={value.player.id}
                  disabled={ratings.some(
                    (rating) => rating.player.id === value.player.id,
                  )}
                >
                  {value.player.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <ul className="space-y-3">
          {ratings.map((rating, index) => (
            <li
              key={rating.player.id}
              className="grid gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-800 sm:grid-cols-[1fr_8rem_2fr_auto] sm:items-end"
            >
              <p className="font-semibold">{rating.player.name}</p>
              <label className="text-sm">
                Rating
                <input
                  disabled={!writable}
                  className="mt-1 min-h-11 w-full rounded-md border bg-white px-2 dark:border-gray-700 dark:bg-gray-900"
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={rating.rating}
                  onChange={(event) =>
                    change(index, { rating: Number(event.target.value) })
                  }
                />
              </label>
              <label className="text-sm">
                Notes
                <input
                  disabled={!writable}
                  className="mt-1 min-h-11 w-full rounded-md border bg-white px-2 dark:border-gray-700 dark:bg-gray-900"
                  maxLength={2000}
                  value={rating.notes}
                  onChange={(event) =>
                    change(index, { notes: event.target.value })
                  }
                />
              </label>
              {writable && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setRatings((value) =>
                      value.filter((_, position) => position !== index),
                    );
                    setDirty(true);
                  }}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
        {ratings.length === 0 && <p>No player ratings.</p>}
        {writable && (
          <Button
            loading={save.isPending}
            loadingLabel="Saving player ratings"
            disabled={!dirty}
            onClick={() => save.mutate()}
          >
            Save ratings
          </Button>
        )}
        {save.isSuccess && <p role="status">Ratings saved.</p>}
        {save.isError && (
          <p role="alert" className="text-red-700 dark:text-red-300">
            {getV2Error(save.error).message}
          </p>
        )}
      </div>
    </SectionQuery>
  );
};
export default RatingsEditor;
